import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Heart, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";

export default function ClaimDetails() {
  const { token } = useParams();
  const [ask, setAsk] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    title: "", first_name: "", last_name: "", address_line: "", postcode: "", tax_id: "",
    declaration: true,
  });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/public/claim-details/${token}`);
      setAsk(res.data);
      setForm((f) => ({ ...f, ...(res.data.held || {}) }));
    } catch (e) { setError(apiError(e)); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/public/claim-details/${token}`, form);
      setDone(res.data);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <div className="panel max-w-md p-8 text-center" data-testid="claim-details-error">
          <Lock className="mx-auto h-7 w-7 text-muted-foreground" />
          <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
            This link cannot be used
          </h1>
          <p className="mt-2 text-[12.5px] text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!ask) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4">
        <div className="panel max-w-md p-8 text-center" data-testid="claim-details-done">
          <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
          <h1 className="mt-4 text-base font-semibold text-foreground md:text-lg">
            Thank you — that is everything
          </h1>
          <p className="mt-2 text-[12.5px] text-muted-foreground">
            {done.organisation} can now claim the tax relief on your gift. Nothing further is needed
            from you.
          </p>
        </div>
      </div>
    );
  }

  const field = (key) => (ask.fields || []).some((f) => f.key === key);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border bg-card/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-2xl items-center gap-2.5 px-4 py-3">
          <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-gradient-to-br from-primary to-[hsl(var(--teal))]">
            <Heart className="h-3.5 w-3.5 text-white" />
          </span>
          <div className="text-[13px] font-semibold text-foreground">{ask.organisation}</div>
          <span className="ml-auto flex items-center gap-1 text-[10.5px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3" /> Secure single-use link
          </span>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-10">
        <div className="kicker">{ask.regime_label}</div>
        <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-foreground">
          One last detail and we can claim the tax on your gift
        </h1>
        <p className="mt-2 text-[12.5px] text-muted-foreground">
          Thank you for {ask.gift}. {ask.authority} needs{" "}
          {(ask.fields || []).map((f) => f.label.toLowerCase()).join(" and ")} before{" "}
          {ask.organisation} can claim on it. This link works until {ask.expires_on} and can only be
          used once.
        </p>

        <div className="panel mt-6 space-y-4 p-6" data-testid="claim-details-form">
          {field("first_name") || field("last_name") ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <F label="Title">
                <Input value={form.title} data-testid="ask-title"
                       onChange={(e) => setForm({ ...form, title: e.target.value })}
                       className="bg-muted/40" />
              </F>
              <F label="First name">
                <Input value={form.first_name} data-testid="ask-first-name"
                       onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                       className="bg-muted/40" />
              </F>
              <F label="Last name">
                <Input value={form.last_name} data-testid="ask-last-name"
                       onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                       className="bg-muted/40" />
              </F>
            </div>
          ) : null}
          {field("address_line") && (
            <F label="House name or number and street">
              <Input value={form.address_line} data-testid="ask-address"
                     onChange={(e) => setForm({ ...form, address_line: e.target.value })}
                     className="bg-muted/40" />
            </F>
          )}
          {field("postcode") && (
            <F label="Postcode" hint="Exactly as it appears on your bank statement.">
              <Input value={form.postcode} data-testid="ask-postcode"
                     onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                     className="bg-muted/40 font-mono text-[12px]" />
            </F>
          )}
          {field("tax_id") && (
            <F label="PAN" hint="Needed on Form 10BD so you can claim the 80G deduction.">
              <Input value={form.tax_id} data-testid="ask-tax-id"
                     onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                     className="bg-muted/40 font-mono text-[12px]" />
            </F>
          )}

          <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border bg-muted/20 p-3">
            <Checkbox checked={form.declaration} data-testid="ask-declaration"
                      onCheckedChange={(v) => setForm({ ...form, declaration: !!v })} />
            <span className="text-[11.5px] text-muted-foreground">
              I confirm I am a taxpayer and {ask.organisation} may claim the tax relief on this gift.
            </span>
          </label>

          <Button onClick={submit} disabled={busy} data-testid="ask-submit"
                  className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send my details
          </Button>
          <p className="text-center text-[10.5px] text-muted-foreground">
            Used only for the claim. Nothing else about your gift changes.
          </p>
        </div>
      </main>
    </div>
  );
}

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] font-medium text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground/80">{hint}</p>}
  </div>
);
